import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { useHistory } from 'react-router-dom';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import {
  aobfStep2,
  updateCompanyOnBoarding,
} from '../../services/company_services';
import { addSurvey, addSurveyQuestionDefault } from '../../services/survey_services';

const styles = () => ({
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

const CompanyOnboardingStep2 = () => {
  const {
    editAccountInformation,
    accountDetails,
    currentUserInfo,
    asLoggedUserDetails,
  } = useAccount();

  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = styles();
  const history = useHistory();

  const [surveyQuestion, setSurveyQuestion] = useState('');
  const [currentGoal, setCurrentGoal] = useState('');
  const [storyAndMission, setStoryAndMission] = useState('');
  const [socialMediaUsage, setSocialMediaUsage] = useState('');
  const [socialMediaHandle, setSocialMediaHandle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const disableButton = () => {
    return (
      isLoading ||
      !surveyQuestion.trim() ||
      !currentGoal.trim() ||
      !storyAndMission.trim()
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disableButton()) {
      setOpenSnackbar(true);
      return;
    }

    setIsLoading(true);
    try {
      const survey = await addSurvey({
        COMPANY_ID: accountDetails?.id,
        ADMIN_ID: currentUserInfo?.username,
      });
      await addSurveyQuestionDefault({
        COMPANY_ID: accountDetails?.id,
        SURVEY_ID: survey.data.addSurvey.id,
        BODY: surveyQuestion,
      });

      // Save other onboarding data
      const step2Data = {
        CONTACT_ID: asLoggedUserDetails.userId,
        CURRENT_GOAL: currentGoal,
        STORY_AND_MISSION: storyAndMission,
        PRIMARY_SOCIAL_MEDIA_APP: socialMediaUsage,
        PRIMARY_SOCIAL_MEDIA_HANDLE: socialMediaHandle,
      };
      console.log('Step 2 Data:', step2Data);
      await aobfStep2(step2Data);

      // Mark onboarding step complete
      // await updateCompanyOnBoarding({
      //   CONTACT_ID: asLoggedUserDetails.userId,
      //   ON_BOARDING_STATE: true,
      // });

      // Update context
      editAccountInformation({
        currentGoal,
        storyAndMission,
        primarySocialMediaApp: socialMediaUsage,
        primarySocialMediaHandle: socialMediaHandle,
      });

      history.push('/company/onboarding/step3');
    } catch (error) {
      console.error(error);
      setOpenSnackbar(true);
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2, mb: '100px' }}>
      <Grid>
        <Grid {...classes.gridItemProps}>
          <Typography
            sx={{
              fontSize: isXS ? '24px' : '28px',
              fontWeight: 700,
              letterSpacing: '-0.43px',
              textAlign: 'center',
              mb: '12px',
            }}
          >
            How could your advisory board be most helpful?
          </Typography>
          <Typography
            sx={{
              fontStyle: 'italic',
              fontSize: '16px',
              fontWeight: 300,
              lineHeight: '24px',
              letterSpacing: '-0.43px',
              color: '#646D7A',
              marginBottom: '18px',
              textAlign: 'center',
            }}
          >
            Let’s start with a few questions advisors can answer when they join
            your board!
          </Typography>
        </Grid>

        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            What is a Yes/No survey question that is a simple way for advisors
            to get started?
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            placeholder={`For example… please add 1. \nHave you ever done a medical visit virtually?\nHave you eaten at our restaurant before?\nHave you used AI for marketing campaigns?`}
            value={surveyQuestion}
            onChange={(e) => setSurveyQuestion(e.target.value)}
            sx={classes.textField}
            multiline
          />
        </Grid>

        {/* Current Challenge or Goal */}
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            What is your current biggest challenge, goal, or opportunities?
            Describe it in a question, so advisors can answer to provide fresh
            ideas, perspectives introductions, and more.
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            placeholder="For example… 
What do you think is the best way to raise capital right now for a startup? 
Any ideas around using AI in marketing campaigns? 
Can anyone make an introduction for me to any venture capitalists?"
            value={currentGoal}
            onChange={(e) => setCurrentGoal(e.target.value)}
            sx={classes.textField}
            multiline
            minRows={2}
          />
        </Grid>

        {/* Story and Mission */}
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            Please add a business description (if for a business or team) or
            personal biography (if for a personal side project) here to give
            advisors a bit more background.
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            value={storyAndMission}
            onChange={(e) => setStoryAndMission(e.target.value)}
            sx={classes.textField}
            multiline
            rows={4}
          />
        </Grid>

        {/* Social Media Usage */}
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            Which social media platform are you most active on or have the most
            followers? (optional)
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            value={socialMediaUsage}
            onChange={(e) => setSocialMediaUsage(e.target.value)}
            sx={classes.textField}
          />
        </Grid>

        {/* Social Media Handle */}
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            What is your username/handle/tag? (optional)
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} mb="8px">
          <TextField
            fullWidth
            value={socialMediaHandle}
            onChange={(e) => setSocialMediaHandle(e.target.value)}
            sx={classes.textField}
          />
        </Grid>

        <Grid {...classes.gridItemProps} md={5} display="flex" justifyContent="center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={disableButton()}
            sx={{
              borderRadius: '50px',
              backgroundColor: '#3171F6',
              '&:hover': { backgroundColor: '#3171F6' },
              height: '48px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 700,
              maxWidth: '450px',
              mb: isSM ? '24px' : null,
              my: '20px',
            }}
          >
            {isLoading ? <LoadingLogoSkeleton /> : 'Next - Last Step!'}
          </Button>
        </Grid>
      </Grid>

      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Please complete all required fields"
      />
    </Box>
  );
};

export default CompanyOnboardingStep2;
